/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { ComponentType, ReactNode } from 'react';

import classNames from 'classnames';
import type { IconProps } from '@douyinfe/semi-icons';
import {
  IconAlertCircle,
  IconInbox,
  IconPlusStroked,
} from '@douyinfe/semi-icons';

import styles from './abr-empty-glyph.module.less';

type AbrEmptyIconComponent = ComponentType<IconProps>;

/** Default ON — set VITE_ABR_REBRAND=false to restore Coze branding. */
function isAbrRebrandEnabled(): boolean {
  const raw = import.meta.env.VITE_ABR_REBRAND;
  if (raw === undefined || raw === '') {
    return true;
  }
  return raw !== 'false' && raw !== '0';
}

export interface AbrEmptyGlyphProps {
  icon: AbrEmptyIconComponent;
  variant?: 'default' | 'muted' | 'error';
  className?: string;
}

export function AbrEmptyGlyph({
  icon: iconComponent,
  variant = 'default',
  className,
}: AbrEmptyGlyphProps) {
  const Icon = iconComponent;
  return (
    <div className={classNames(styles.glyph, styles[variant], className)}>
      <Icon size="extra-large" />
    </div>
  );
}

export type AbrEmptyGlyphKind = 'empty' | 'error' | 'add';

const EMPTY_GLYPH_ICONS: Record<AbrEmptyGlyphKind, AbrEmptyIconComponent> = {
  empty: IconInbox,
  error: IconAlertCircle,
  add: IconPlusStroked,
};

/** ABR 开启时用品牌图标块替代 Coze 插画；关闭时保留原插画节点 */
export function resolveAbrEmptyIcon(
  cozIllustration: ReactNode,
  kind: AbrEmptyGlyphKind = 'empty',
): ReactNode {
  if (!isAbrRebrandEnabled()) {
    return cozIllustration;
  }

  return (
    <AbrEmptyGlyph
      icon={EMPTY_GLYPH_ICONS[kind]}
      variant={kind === 'error' ? 'error' : 'default'}
    />
  );
}
